import {
    BookingPriceGroup,
    IBookingCheckInData,
    IProductAvailOption,
    ITourFareBookingTime,
    SelectedAvailability,
} from "@interfaces";
import type { AddToCartProductSnapshot } from "@utilities";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import BookNowButton from "./BookNowButton";
import { breakpoint, formatBookingDatesByProductType, theme, pushAddToCart } from "@utilities";
import moment from "moment";
import { calculateRRPOfAvailabilities } from "src/Utilities/cartHelper";
import { ClipLoaderCommon } from "@components";
import {
    getAvailsCheckInOptions,
    getCheckInAvail,
    isMobileViewHandler,
} from "../Checkout/checkoutHelpers";
import { PickUpDetailsMessage, PickUpDetailsButton } from "./PickUpDetails";
import { BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS } from "@constants";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { loadQuotes } from "src/Redux/Slices/allQuotes";


interface AddToCartButtonProps {
    onTogglePopup?: () => void;
    productClass: string;
    selectedAvailabilities: SelectedAvailability[];
    isSubmitInProgress: null | "cart" | "book-now" | "quote";
    increaseQuantity: () => void;
    decreaseQuantity: () => void;
    quantity: number;
    bookingSystem: string;
    selectedBookingTimeId: string;
    bookingDetails: ITourFareBookingTime;
    handleAdd: (
        selectedAvailabilities: SelectedAvailability[],
        mode: "addToCart" | "bookNow" | "newQuote" | "existingQuote",
        timeId?: IProductAvailOption,
        pickUpLocation?: IProductAvailOption,
        dropOffLocation?: IProductAvailOption,
    ) => void;
    addToCartRef?: React.Ref<HTMLDivElement>;
    productPriceGroup: BookingPriceGroup;
    /** Minimal product fields for GTM add_to_cart. */
    addToCartProductSnapshot?: AddToCartProductSnapshot | null;
}


export const AddToCartButton = forwardRef<HTMLDivElement, AddToCartButtonProps>(
    (
        {
            handleAdd,
            productClass,
            addToCartProductSnapshot,
            selectedAvailabilities,
            increaseQuantity,
            decreaseQuantity,
            quantity,
            bookingSystem,
            selectedBookingTimeId,
            bookingDetails,
            isSubmitInProgress,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            productPriceGroup,
        }: AddToCartButtonProps,
        addToCartRef,
    ) => {
        const dispatch = useDispatch();
        const [isOpen, setIsOpen] = useState(false);
        const [isDetailsOpen, setIsDetailsOpen] = useState(false);
        const isMobileView = isMobileViewHandler();
        // Calculate the total price — quantity already includes numPax multiples.
        const rawPrice = calculateRRPOfAvailabilities(selectedAvailabilities, quantity);
        const totalPrice = rawPrice;
        const checkInAvail = useMemo(
            () => getCheckInAvail(selectedAvailabilities),
            [selectedAvailabilities],
        );
        const [isCheckInOptionsLoading, setIsCheckInOptionsLoading] = useState(false);
        const [checkInOptions, setCheckInOptions] = useState<IBookingCheckInData | null>();

        // Toggle dropdown for additional actions
        const handleDropdownToggle = (event: React.MouseEvent) => {
            event.stopPropagation();
            setIsOpen(!isOpen);
        };

        const handleAddToCart = async (
            mode: "addToCart" | "bookNow" | "existingQuote" | "newQuote",
        ) => {
            if (
                addToCartProductSnapshot &&
                (mode === "addToCart" || mode === "bookNow") &&
                selectedAvailabilities.length > 0
            ) {
                const travelDate = moment(selectedAvailabilities[0].BookingDate).format("MMMM D");
                const rawPrice = calculateRRPOfAvailabilities(selectedAvailabilities, quantity);
                const value = rawPrice;
                pushAddToCart(addToCartProductSnapshot, {
                    value,
                    travelDate,
                    ctaClick: mode === "bookNow" ? "Book Now" : "Add to Cart",
                });
            }
            try {
                await handleAdd(selectedAvailabilities, mode);
                if (mode === "newQuote" || mode === "existingQuote") {
                    dispatch(loadQuotes() as any); // refresh list immediately
                }
            } catch (e) {
                console.error("handleAdd failed:", e);
            }
        };

        useEffect(() => {
            if (checkInAvail) {
                setIsCheckInOptionsLoading(true);
                getAvailsCheckInOptions(
                    bookingSystem,
                    getCheckInAvail(selectedAvailabilities)!,
                    bookingDetails,
                )
                    .then(setCheckInOptions)
                    .catch((error) => {
                        console.error(error);
                        ErrorToast({ Message: `Failed to load pickup details` });
                    })
                    .finally(() => setIsCheckInOptionsLoading(false));
            }
            return () => setCheckInOptions(null);
        }, [checkInAvail]);

        const quantityField = (
            <div className="price-container">
                <div className="price">
                    Price <span>${totalPrice.toFixed(2)}</span>
                </div>
                <div className="quantity">
                    Qty
                    <button onClick={decreaseQuantity}>
                        <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
                            <path
                                d="M1 1H9"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                    <span>{quantity}</span>
                    <button onClick={increaseQuantity}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path
                                d="M5 1V9M1 5H9"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        );

        const isWithCustomBookingTimeIDs = BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS.includes(
            bookingSystem.trim().toLowerCase(),
        );
        const pickUpDetailsComp = (
            <PickUpDetailsMessage
                isWithCustomBookingTimeIDs={isWithCustomBookingTimeIDs}
                selectedBookingTimeId={selectedBookingTimeId}
                checkInOptions={checkInOptions}
            />
        );

        // show pick up details button only loading has been complete and ther are check in options
        const pickUpDetailsButton = !isCheckInOptionsLoading &&
            !!checkInOptions?.pickupLocations?.flatMap((pl) => pl.options).length && (
                <PickUpDetailsButton
                    isDetailsOpen={isDetailsOpen}
                    isMobileView={isMobileView}
                    toggleDetails={() => setIsDetailsOpen((prev) => !prev)}
                    pickUpDetailsComp={pickUpDetailsComp}
                />
            );

        return (
            <Secondary ref={addToCartRef}>
                <div className="text-container">
                    <div className="secondary-container">
                        <div className="name-container">
                            {(() => {
                                const formattedDates = formatBookingDatesByProductType(
                                    selectedAvailabilities,
                                    productClass!,
                                );
                                return formattedDates ? `(${formattedDates})` : "";
                            })()}
                        </div>
                        <div className="clear">
                            <span className="qty-condition">
                                *Quantity applies to all selected dates
                            </span>
                            {!isMobileView && pickUpDetailsButton}
                        </div>
                        {isMobileView && quantityField}
                        {isMobileView && pickUpDetailsButton}
                    </div>
                </div>

                {!isMobileView && isDetailsOpen && (
                    <div className="container-background">{pickUpDetailsComp}</div>
                )}
                <ButtonContainer>
                    {!isMobileView && quantityField}
                    <BookNowButton
                        handleClick={() => handleAddToCart("bookNow")}
                        disabled={isSubmitInProgress !== null || isCheckInOptionsLoading}
                        isLoading={isSubmitInProgress === "book-now" || isCheckInOptionsLoading}
                    />

                    <AddButton>
                        <div
                            className={`button-container ${isSubmitInProgress !== null || isCheckInOptionsLoading ? "disabled" : ""}`}
                        >
                            <div
                                className="add-to-cart-button"
                                onClick={() => handleAddToCart("addToCart")}
                                data-gtm-id="add-to-cart"
                            >
                                {isSubmitInProgress === "cart" || isCheckInOptionsLoading ? (
                                    <ClipLoaderCommon
                                        size={24}
                                        color={theme.colors.highlight}
                                        margin={0}
                                    />
                                ) : (
                                    "Add to Cart"
                                )}
                            </div>
                            <div className="divider" />
                            {isSubmitInProgress === "quote" || isCheckInOptionsLoading ? (
                                <div className="dropdown-toggle"></div>
                            ) : (
                                <button
                                    className={`dropdown-toggle ${isOpen ? "open" : ""}`}
                                    onClick={handleDropdownToggle}
                                    disabled={isSubmitInProgress !== null}
                                >
                                    <img
                                        src="/images/chevron-up.svg"
                                        alt="chevron"
                                        className={`chevron ${isOpen ? "rotated" : ""}`}
                                    />
                                </button>
                            )}
                        </div>
                        {isOpen && isSubmitInProgress === null && (
                            <div className="dropdown-menu">
                                <div
                                    className="dropdown-item"
                                    onClick={() => handleAddToCart("existingQuote")}
                                    data-gtm-id="add-to-existing-quote"
                                >
                                    Add to existing quote
                                </div>
                                <div
                                    className="dropdown-item"
                                    onClick={() => handleAddToCart("newQuote")}
                                    data-gtm-id="save-as-new-quote"
                                >
                                    Save as new quote
                                </div>
                            </div>
                        )}
                    </AddButton>
                </ButtonContainer>
            </Secondary>
        );
    },
);

const Secondary = styled.div`
    background-color: #fff;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    margin: auto;

    ${breakpoint.breakOnlyMobile`
        flex-direction: column;
        align-items: stretch;
        width: 100%;
        box-sizing: border-box;
  `}

    ${breakpoint.breakIpadPro`
        width: ${(p) => p.theme.widths.wide_900};
    `}

    ${breakpoint.breakLarge`
        width: ${(p) => p.theme.widths.wide_1110};
    `}

    .text-container {
        display: flex;
        flex-direction: column;
        margin-bottom: 15px;
        width: 100%;
    }

    .secondary-container {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }
    .clear {
        display: flex;
        font-size: 14px;
        color: ${theme.colors.main};
        justify-content: flex-end;
        .qty-condition {
            color: #000000 !important;
            font-size: 12px;
            text-align: right;

            ${breakpoint.breakOnlyMobile`
                color: #6E7375 !important;
                font-size: 10px;
            `}
            ${breakpoint.breakTablet`
                flex: 1;
            `}
        }

        .pick-up-wrapper {
            flex: 1;
        }
    }
    .name-container {
        font-weight: bold;
        font-size: 18px;
        p {
            font-size: 14px;
            font-weight: 500;
        }
    }
    .price-container {
        border: 1px solid #c3c9d0;
        border-radius: 8px;
        display: flex;
        justify-content: space-between;
        padding: 8px 20px;
        align-items: center;
        .price {
            display: flex;
            flex-direction: column;
            color: #6e7375;
            font-size: 12px;
            span {
                color: #000;
                font-size: 18px;
            }
        }
        .quantity {
            color: #6e7375;
            font-size: 12px;
            display: flex;
            align-items: center;
            gap: 10px;
            button {
                height: 20px;
                width: 20px;
                background-color: ${theme.colors.main};
                border-radius: 50%;
                display: flex;
                justify-content: center;
                align-items: center;
                border: none;
                cursor: pointer;
                padding: 0;
                svg {
                    color: ${theme.colors.button_text};
                }
            }
            span {
                font-size: 20px;
                color: #1f2937;
                width: 18px;
                text-align: center;
            }
        }
    }
    .container-background {
        background-color: #f4f6f7;
        border-radius: 4px;
        padding: 14px;
        height: 200px;
        overflow-y: auto;
        ${breakpoint.breakTablet`
            margin-bottom: 15px;
        `}
    }
    .details-container {
        font-size: 14px;
        font-weight: 500;
        color: #232b2b;
        display: flex;
        align-items: center;
        gap: 10px;
        ${breakpoint.breakTablet`
            justify-content: flex-end;
        `}
        .chevron {
            transition: transform 0.2s ease-in-out;
        }
        .chevron.rotated {
            transform: rotate(180deg);
        }
    }

    .details-content {
        margin-top: 8px;
        font-size: 14px;
        line-height: 1.5;
    }

    .time-container {
        display: flex;
        flex-direction: column;
        gap: 12px;
        p {
            font-size: 18px;
        }
        .time {
            display: flex;
            gap: 32px;
            span {
                display: flex;
                gap: 8px;
                font-weight: 600;
            }
        }
    }

    .formControlResponsive {
        width: 500px;

        @media (min-width: 375px) {
            width: 342px;
        }
        @media (min-width: 425px) {
            width: 392px;
        }
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 15px;
    width: 100%;
    box-sizing: border-box;
    -webkit-box-orient: horizontal;
    -webkit-box-direction: normal;
    -moz-box-orient: horizontal;
    -moz-box-direction: normal;

    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 10px;
    ${breakpoint.breakTablet`
    flex-direction: row;

    > :first-child {
      flex: 1;
    }

    > :not(:first-child) {
      flex: 0.5;
    }

    .price-container {
        padding-top: 2px;
        padding-bottom: 2px;
    }
  `}
`;

const AddButton = styled.div`
    width: 250px;
    flex-shrink: 0;

    ${breakpoint.breakOnlyMobile`
    width: 100%;
    max-width: 100%;
    min-height: 54px;
  `}

    position: relative;

    .button-container {
        border-radius: 4px;
        width: 100%;
        display: flex;
        align-items: center;
        overflow: auto;
        justify-content: space-between;
        background: linear-gradient(
            to right,
            ${theme.gradients.button[0]},
            ${theme.gradients.button[1]}
        );
        height: 54px;
        position: relative;
        padding: 0 12px;
        transition:
            background 0.3s ease,
            opacity 0.3s ease;
    }

    .button-container.disabled {
        background: linear-gradient(to right, #a0a0a0, #bdbdbd);
        opacity: 0.6;
        pointer-events: none;
    }

    .add-to-cart-button {
        border: none;
        background: transparent;
        color: ${theme.colors.button_text};
        font-weight: bold;
        cursor: pointer;
        text-align: center;
        flex-grow: 1;
        height: 54px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .divider {
        height: 32px;
        width: 1px;
        background-color: ${theme.colors.button_text};
        margin: 0 8px;
    }
    .dropdown-toggle {
        border: none;
        background: transparent;
        color: white;
        cursor: pointer;
        height: 54px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: auto;
    }
    .chevron {
        transition: transform 0.2s ease-in-out;
    }
    .chevron.rotated {
        transform: rotate(180deg);
    }
    .dropdown-menu {
        position: absolute;
        bottom: 110%;
        right: 0;
        width: 100%;
        background: white;
        box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
        border: 1px solid #ddd;
        z-index: 10;
    }
    .dropdown-item {
        padding: 12px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        text-align: center;
        color: black;
        border-bottom: 1px solid #eee;
    }
    .dropdown-item:last-child {
        border-bottom: none;
    }
    .dropdown-item:hover {
        background: #f7f7f7;
    }
    .overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(9, 10, 24, 0.4);
        z-index: 9999;
    }
    .modal {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #fff;
        padding: 38px 46px;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
        width: 100%;
        max-width: 732px;
        z-index: 10000;
    }
    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .modal-header h2 {
        font-size: 28px;
        font-weight: 600;
    }
    .close-btn {
        background: transparent;
        position: absolute;
        top: 8%;
        right: 3%;
        border: none;
        color: #000;
        font-size: 20px;
        cursor: pointer;
    }
    .modal-body label {
        font-size: 14px;
        margin-bottom: 5px;
    }
    .modal-body {
        margin-top: 15px;
        width: 100%;
        padding: 10px;
        font-size: 14px;
    }
    .modal-footer {
        margin-top: 20px;
        display: flex;
        justify-content: center;
    }
    .create-btn {
        width: 216px;
        font-size: 16px;
        height: 54px;
        background: linear-gradient(
            to right,
            ${theme.gradients.button[0]},
            ${theme.gradients.button[1]}
        );
        color: white;
        font-weight: bold;
        border: none;
        cursor: pointer;
    }
    .create-btn:hover {
        background: linear-gradient(
            to right,
            ${theme.gradients.buttonReversed[0]},
            ${theme.gradients.buttonReversed[1]}
        );
    }
`;
